import {useEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {CloseIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {Highlight, Text} from "@pg-design/text-module";

import {prettyScroll} from "../../../../styles/receipes/pretty_scroll";
import {getPropertyDetailApi, useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {leadModalZindex} from "../../constants/lead/lead_modal_zindex";
import {closeTinderMultiLeadModal, rejectCurrentTinderMultiLeadProperty, showTinderMultiLeadSummary} from "../../state/tinder_multi_lead_slice";
import {hitTinderMultiLeadModalGtmEvent, hitTinderMultiLeadSummaryGtmEvent, hitTinderMultiLeadTopicGtmEvent} from "./tracking/hit_tinder_multi_lead_gtm_events";
import {trackTinderMultiLeadRate, trackTinderMultiLeadSurveyClose, trackTinderMultiLeadSurveyStart} from "./tracking/track_tinder_multi_lead_events";
import {TinderMultiLeadAskSheet} from "./TinderMultiLeadAskSheet";
import {TinderMultiLeadBottomBar} from "./TinderMultiLeadBottomBar";
import {TinderMultiLeadDeckPlaceholder} from "./TinderMultiLeadDeckPlaceholder";
import {TINDER_MULTI_LEAD_MAX_SHADOWS, TinderMultiLeadDeckShadows} from "./TinderMultiLeadDeckShadows";
import {TinderMultiLeadOfferCard} from "./TinderMultiLeadOfferCard";
import {TinderMultiLeadSuccessHeader} from "./TinderMultiLeadSuccessHeader";
import {TinderMultiLeadSummary} from "./TinderMultiLeadSummary";
import {TinderMultiLeadSwipeCard} from "./TinderMultiLeadSwipeCard";
import {useTinderMultiLeadAnimation} from "./use_tinder_multi_lead_animation";
import {useTinderMultiLeadQueue} from "./use_tinder_multi_lead_queue";
import {useTinderMultiLeadSend} from "./use_tinder_multi_lead_send";

export function TinderMultiLeadModal() {
    const dispatch = useAppDispatch();
    const modalState = useAppSelector((state) => state.tinderMultiLead);
    const pageProperty = useAppSelector((state) => state.property.property);
    const variables = useAppSelector((state) => state.variables.data);
    const prefetchPropertyDetail = getPropertyDetailApi.usePrefetch("getPropertyDetail");
    const isPagePropertyOrigin = Boolean(pageProperty && pageProperty.id === modalState.originPropertyId);
    const {data: fetchedOriginProperty} = useGetPropertyDetailQuery(modalState.originPropertyId, {
        skip: !modalState.isOpen || !modalState.originPropertyId || isPagePropertyOrigin
    });
    const originProperty = isPagePropertyOrigin ? pageProperty : fetchedOriginProperty || null;
    const {activeProperty, isLoading: isQueueLoading, isSuccess: isQueueSuccess, queue} = useTinderMultiLeadQueue(originProperty, modalState);
    const {isContactDataLoading, sendTinderMultiLeadApplication} = useTinderMultiLeadSend();
    const {contentRef, frozenHeight, handleCardReady, handleExitComplete, isAdvancing, setDeckNode, shouldReduceMotion} = useTinderMultiLeadAnimation({
        activePropertyId: activeProperty?.id,
        currentIndex: modalState.currentIndex,
        isOpen: modalState.isOpen,
        step: modalState.step
    });
    const [isAskSheetOpen, setIsAskSheetOpen] = useState(false);
    const [successVendorName, setSuccessVendorName] = useState<string | null>(null);
    const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const askSelectedQuestionsRef = useRef<string[]>([]);

    const isAsking = Boolean(activeProperty && modalState.submittingPropertyId === activeProperty.id);
    const askVendorName = activeProperty?.offer.vendor.name ?? "";
    const askVendorLogoUrl = activeProperty?.offer.vendor.logo?.v_log_80x60 ?? null;
    const shadowKeys = queue.slice(modalState.currentIndex + 1, modalState.currentIndex + 1 + TINDER_MULTI_LEAD_MAX_SHADOWS).map((property) => property.id);
    const hasDeckBehind = shadowKeys.length > 0;
    // Keep the shadow backing space reserved while advancing TOWARD a real card, so the front card
    // never jumps when the last shadow is consumed mid-transition. Excluding the terminal advance
    // (no incoming card) keeps the catcher top/height consistent with the frozen, backing-less height.
    const showBacking = hasDeckBehind || (isAdvancing && Boolean(activeProperty));

    // Warm the next offers' detail cache so a promoted card shows content instead of a loader.
    useEffect(() => {
        if (!modalState.isOpen) {
            return;
        }

        for (let offset = 1; offset <= 2; offset += 1) {
            const upcoming = queue[modalState.currentIndex + offset];

            if (upcoming) {
                prefetchPropertyDetail(upcoming.id);
            }
        }
    }, [modalState.isOpen, modalState.currentIndex, queue, prefetchPropertyDetail]);

    useEffect(() => {
        if (!modalState.isOpen || modalState.step !== "browse" || !isQueueSuccess) {
            return;
        }

        if (!activeProperty) {
            if (modalState.sentApplications.length) {
                dispatch(showTinderMultiLeadSummary());
                return;
            }

            dispatch(closeTinderMultiLeadModal());
        }
    }, [activeProperty, dispatch, isQueueSuccess, modalState.isOpen, modalState.sentApplications.length, modalState.step]);

    // Close the ask sheet whenever the deck advances (a successful send bumps currentIndex) or the
    // modal is reset/reopened (currentIndex returns to 0).
    useEffect(() => {
        setIsAskSheetOpen(false);
    }, [modalState.currentIndex]);

    useEffect(() => {
        if (!modalState.isOpen) {
            setSuccessVendorName(null);
        }
    }, [modalState.isOpen]);

    useEffect(() => {
        if (modalState.isOpen && modalState.step === "browse" && activeProperty) {
            hitTinderMultiLeadModalGtmEvent({action: "view"});
        }
    }, [activeProperty?.id, modalState.isOpen, modalState.step]);

    useEffect(() => {
        if (modalState.isOpen && modalState.step === "summary") {
            hitTinderMultiLeadSummaryGtmEvent({action: "view", offersCount: modalState.sentApplications.length});
        }
        // sentApplications is final once the summary step is reached, so it can't retrigger this effect.
    }, [modalState.isOpen, modalState.step, modalState.sentApplications.length]);

    useEffect(() => {
        return () => {
            if (successTimeoutRef.current) {
                clearTimeout(successTimeoutRef.current);
            }
        };
    }, []);

    if (!modalState.isOpen) {
        return null;
    }

    const isSummaryStep = modalState.step === "summary";

    const closeModal = () => {
        dispatch(closeTinderMultiLeadModal());
    };

    // User-initiated dismissal (X button / ESC) — the summary "Powróć do poszukiwań" CTA goes through
    // closeModal directly, tracking its own summary `click` instead of a `close`.
    const handleClose = () => {
        if (isSummaryStep) {
            hitTinderMultiLeadSummaryGtmEvent({action: "close", offersCount: modalState.sentApplications.length});
        } else {
            hitTinderMultiLeadModalGtmEvent({action: "close"});
        }

        closeModal();
    };

    const handleReject = () => {
        if (!activeProperty || isAsking || isContactDataLoading || isAdvancing) {
            return;
        }

        hitTinderMultiLeadModalGtmEvent({action: "click", clickText: "Odrzuć"});
        trackTinderMultiLeadRate({property: activeProperty, value: "dislike"});
        dispatch(rejectCurrentTinderMultiLeadProperty(activeProperty.id));
    };

    const handleAsk = () => {
        if (!activeProperty || isAsking || isContactDataLoading || isAdvancing) {
            return;
        }

        askSelectedQuestionsRef.current = [];
        hitTinderMultiLeadModalGtmEvent({action: "click", clickText: "Zapytaj"});
        hitTinderMultiLeadTopicGtmEvent({action: "view"});
        trackTinderMultiLeadRate({property: activeProperty, value: "like"});
        trackTinderMultiLeadSurveyStart({property: activeProperty});
        setIsAskSheetOpen(true);
    };

    const handleAskSheetClose = () => {
        if (activeProperty) {
            trackTinderMultiLeadSurveyClose({property: activeProperty, selectedQuestionValues: askSelectedQuestionsRef.current});
        }

        hitTinderMultiLeadTopicGtmEvent({action: "close"});
        setIsAskSheetOpen(false);
    };

    const handleAskSubmit = async (additionalQuestions: {value: string; label: string}[]) => {
        if (!activeProperty || isAsking || isContactDataLoading) {
            return;
        }

        hitTinderMultiLeadTopicGtmEvent({action: "submit", selectedOptions: additionalQuestions.map((question) => question.label)});

        const sentVendorName = activeProperty.offer.vendor.name;

        const application = await sendTinderMultiLeadApplication({
            additionalQuestions,
            property: activeProperty,
            refererApplicationUuid: modalState.refererApplicationUuid,
            sourceSection: modalState.sourceSection
        });

        if (application) {
            if (successTimeoutRef.current) {
                clearTimeout(successTimeoutRef.current);
            }

            setSuccessVendorName(sentVendorName);
            successTimeoutRef.current = setTimeout(() => setSuccessVendorName(null), 4000);
        }
    };

    return (
        <>
            <Modal
                isOpen={modalState.isOpen}
                onModalClose={handleClose}
                shouldCloseOnOverlayClick={false}
                zIndex={leadModalZindex}
                showCloseButton={false}
                fullScreen="xs"
                vAlign={isSummaryStep ? "center" : "bottom"}
                className={isSummaryStep ? modalContentSummaryStyle : modalContentStyle}
                contentLabel="Przejrzyj i zapytaj"
            >
                <div className={modalShellStyle}>
                    <button type="button" className={closeButtonStyle} onClick={handleClose} aria-label="Zamknij">
                        <CloseIcon size="1.6" wrapperSize="1.6" fill="currentColor" />
                    </button>

                    {modalState.step === "summary" ? (
                        <TinderMultiLeadSummary sentApplications={modalState.sentApplications} onClose={closeModal} />
                    ) : (
                        <>
                            <div className={headerRegionStyle}>
                                <header className={headerStyle}>
                                    <Text variant="headline_3" as="h2" color="var(--colors-secondary)">
                                        <Highlight>Przejrzyj i zapytaj</Highlight>
                                    </Text>
                                    <Text variant="body_copy_2" color="var(--colors-gray-700)">
                                        Przyspiesz swoje poszukiwania, skorzystaj z naszych rekomendacji.
                                    </Text>
                                </header>
                                {successVendorName && <TinderMultiLeadSuccessHeader reduceMotion={shouldReduceMotion} vendorName={successVendorName} />}
                            </div>

                            <div ref={contentRef} className={cx(contentStyle, isAdvancing ? contentLockedStyle : contentScrollStyle)}>
                                {isQueueLoading ? (
                                    <div className={loaderStyle}>
                                        <Loader size="lg" />
                                    </div>
                                ) : (
                                    <motion.div
                                        ref={setDeckNode}
                                        className={cx(deckStyle, showBacking && deckBackingSpaceStyle)}
                                        animate={{minHeight: isAdvancing && frozenHeight ? frozenHeight : 0}}
                                        transition={{duration: isAdvancing ? 0 : shouldReduceMotion ? 0.18 : 0.4, ease: [0.4, 0, 0.2, 1]}}
                                    >
                                        <TinderMultiLeadDeckShadows reduceMotion={shouldReduceMotion} shadowKeys={shadowKeys} />
                                        <TinderMultiLeadDeckPlaceholder active={isAdvancing} hasDeckBehind={showBacking} reduceMotion={shouldReduceMotion} />

                                        <AnimatePresence mode="popLayout" onExitComplete={handleExitComplete}>
                                            {activeProperty && (
                                                <TinderMultiLeadSwipeCard key={activeProperty.id}>
                                                    <TinderMultiLeadOfferCard
                                                        error={modalState.error}
                                                        index={modalState.currentIndex}
                                                        property={activeProperty}
                                                        variables={variables}
                                                        onReadyChange={handleCardReady}
                                                    />
                                                </TinderMultiLeadSwipeCard>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                )}
                            </div>

                            {activeProperty && (
                                <TinderMultiLeadBottomBar
                                    askDisabled={isAsking || isQueueLoading || isContactDataLoading || isAdvancing}
                                    isAsking={isAsking}
                                    onAsk={handleAsk}
                                    onReject={handleReject}
                                    rejectDisabled={isAsking || isContactDataLoading || isAdvancing}
                                />
                            )}
                        </>
                    )}
                </div>
            </Modal>

            <TinderMultiLeadAskSheet
                isOpen={isAskSheetOpen}
                isSubmitting={isAsking}
                vendorLogoUrl={askVendorLogoUrl}
                vendorName={askVendorName}
                onClose={handleAskSheetClose}
                onSelectedQuestionsChange={(questions) => {
                    askSelectedQuestionsRef.current = questions;
                }}
                onSubmit={handleAskSubmit}
            />
        </>
    );
}

const modalContentStyle = css({
    // The overlay is `position: fixed; inset: 0`, i.e. the VISIBLE viewport. 100vh would overflow it
    // on mobile browsers with dynamic UI (Android nav/URL bar), hiding the bottom bar and card end.
    width: "100%",
    height: "100%",
    maxHeight: "100%",
    overflow: "hidden",
    borderRadius: 0,
    backgroundColor: "var(--colors-white)",
    md: {
        width: "min(985px, calc(100vw - 32px))",
        height: "auto",
        maxHeight: "calc(100vh - 48px)",
        // Docked to the viewport bottom (vAlign="bottom"): round only the top corners.
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0
    }
});

const modalContentSummaryStyle = css({
    width: "100%",
    height: "100%",
    maxHeight: "100%",
    overflow: "hidden",
    borderRadius: 0,
    backgroundColor: "var(--colors-white)",
    md: {
        width: "min(985px, calc(100vw - 32px))",
        height: "auto",
        maxHeight: "calc(100vh - 48px)",
        borderRadius: 2
    }
});

const modalShellStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    maxHeight: "100%",
    backgroundColor: "var(--colors-white)",
    md: {
        minHeight: "auto",
        maxHeight: "calc(100vh - 48px)"
    }
});

const closeButtonStyle = css({
    position: "absolute",
    top: 2,
    right: 2,
    zIndex: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "3.2rem",
    height: "3.2rem",
    border: 0,
    borderRadius: "100%",
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-2)",
    cursor: "pointer"
});

const headerRegionStyle = css({
    position: "relative"
});

const headerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    px: 2,
    pt: 2.5,
    pb: 2,
    pr: 8,
    md: {
        px: 4,
        pt: 3,
        pb: 2.5,
        pr: 9
    }
});

const contentStyle = css({
    position: "relative",
    // 80vh is only the PREFERRED size (keeps the modal tall while the deck loads). minHeight: 0 lets
    // the flex item shrink to the space left by the header/bottom bar — a hard minHeight would push
    // the scroll container's bottom past the modal's overflow:hidden edge, making the card end
    // unreachable by scrolling.
    flex: "1 1 80vh",
    minHeight: 0,
    overflowX: "hidden",
    scrollbarGutter: "stable",
    maxWidth: "calc(100% - 1.2rem)",
    px: 1.5,
    pt: 1.5,
    pb: 3,
    md: {
        px: 4,
        pt: 2,
        pb: 3
    }
});

const contentScrollStyle = css(prettyScroll);

const contentLockedStyle = css({
    overflowY: "hidden"
});

const deckStyle = css({
    position: "relative"
});

const deckBackingSpaceStyle = css({
    pt: "56px"
});

const loaderStyle = css({
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "var(--colors-white)"
});
import {useEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {CloseIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {Highlight, Text} from "@pg-design/text-module";

import {prettyScroll} from "../../../../styles/receipes/pretty_scroll";
import {getPropertyDetailApi, useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {leadModalZindex} from "../../constants/lead/lead_modal_zindex";
import {closeTinderMultiLeadModal, rejectCurrentTinderMultiLeadProperty, showTinderMultiLeadSummary} from "../../state/tinder_multi_lead_slice";
import {hitTinderMultiLeadModalGtmEvent, hitTinderMultiLeadSummaryGtmEvent, hitTinderMultiLeadTopicGtmEvent} from "./tracking/hit_tinder_multi_lead_gtm_events";
import {trackTinderMultiLeadRate, trackTinderMultiLeadSurveyClose, trackTinderMultiLeadSurveyStart} from "./tracking/track_tinder_multi_lead_events";
import {TinderMultiLeadAskSheet} from "./TinderMultiLeadAskSheet";
import {TinderMultiLeadBottomBar} from "./TinderMultiLeadBottomBar";
import {TinderMultiLeadDeckPlaceholder} from "./TinderMultiLeadDeckPlaceholder";
import {TINDER_MULTI_LEAD_MAX_SHADOWS, TinderMultiLeadDeckShadows} from "./TinderMultiLeadDeckShadows";
import {TinderMultiLeadOfferCard} from "./TinderMultiLeadOfferCard";
import {TinderMultiLeadSuccessHeader} from "./TinderMultiLeadSuccessHeader";
import {TinderMultiLeadSummary} from "./TinderMultiLeadSummary";
import {TinderMultiLeadSwipeCard} from "./TinderMultiLeadSwipeCard";
import {useTinderMultiLeadAnimation} from "./use_tinder_multi_lead_animation";
import {useTinderMultiLeadQueue} from "./use_tinder_multi_lead_queue";
import {useTinderMultiLeadSend} from "./use_tinder_multi_lead_send";

export function TinderMultiLeadModal() {
    const dispatch = useAppDispatch();
    const modalState = useAppSelector((state) => state.tinderMultiLead);
    const pageProperty = useAppSelector((state) => state.property.property);
    const variables = useAppSelector((state) => state.variables.data);
    const prefetchPropertyDetail = getPropertyDetailApi.usePrefetch("getPropertyDetail");
    const isPagePropertyOrigin = Boolean(pageProperty && pageProperty.id === modalState.originPropertyId);
    const {data: fetchedOriginProperty} = useGetPropertyDetailQuery(modalState.originPropertyId, {
        skip: !modalState.isOpen || !modalState.originPropertyId || isPagePropertyOrigin
    });
    const originProperty = isPagePropertyOrigin ? pageProperty : fetchedOriginProperty || null;
    const {activeProperty, isLoading: isQueueLoading, isSuccess: isQueueSuccess, queue} = useTinderMultiLeadQueue(originProperty, modalState);
    const {isContactDataLoading, sendTinderMultiLeadApplication} = useTinderMultiLeadSend();
    const {contentRef, frozenHeight, handleCardReady, handleExitComplete, isAdvancing, setDeckNode, shouldReduceMotion} = useTinderMultiLeadAnimation({
        activePropertyId: activeProperty?.id,
        currentIndex: modalState.currentIndex,
        isOpen: modalState.isOpen,
        step: modalState.step
    });
    const [isAskSheetOpen, setIsAskSheetOpen] = useState(false);
    const [successVendorName, setSuccessVendorName] = useState<string | null>(null);
    const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const askSelectedQuestionsRef = useRef<string[]>([]);

    const isAsking = Boolean(activeProperty && modalState.submittingPropertyId === activeProperty.id);
    const askVendorName = activeProperty?.offer.vendor.name ?? "";
    const askVendorLogoUrl = activeProperty?.offer.vendor.logo?.v_log_80x60 ?? null;
    const shadowKeys = queue.slice(modalState.currentIndex + 1, modalState.currentIndex + 1 + TINDER_MULTI_LEAD_MAX_SHADOWS).map((property) => property.id);
    const hasDeckBehind = shadowKeys.length > 0;
    // Keep the shadow backing space reserved while advancing TOWARD a real card, so the front card
    // never jumps when the last shadow is consumed mid-transition. Excluding the terminal advance
    // (no incoming card) keeps the catcher top/height consistent with the frozen, backing-less height.
    const showBacking = hasDeckBehind || (isAdvancing && Boolean(activeProperty));

    // Warm the next offers' detail cache so a promoted card shows content instead of a loader.
    useEffect(() => {
        if (!modalState.isOpen) {
            return;
        }

        for (let offset = 1; offset <= 2; offset += 1) {
            const upcoming = queue[modalState.currentIndex + offset];

            if (upcoming) {
                prefetchPropertyDetail(upcoming.id);
            }
        }
    }, [modalState.isOpen, modalState.currentIndex, queue, prefetchPropertyDetail]);

    useEffect(() => {
        if (!modalState.isOpen || modalState.step !== "browse" || !isQueueSuccess) {
            return;
        }

        if (!activeProperty) {
            if (modalState.sentApplications.length) {
                dispatch(showTinderMultiLeadSummary());
                return;
            }

            dispatch(closeTinderMultiLeadModal());
        }
    }, [activeProperty, dispatch, isQueueSuccess, modalState.isOpen, modalState.sentApplications.length, modalState.step]);

    // Close the ask sheet whenever the deck advances (a successful send bumps currentIndex) or the
    // modal is reset/reopened (currentIndex returns to 0).
    useEffect(() => {
        setIsAskSheetOpen(false);
    }, [modalState.currentIndex]);

    useEffect(() => {
        if (!modalState.isOpen) {
            setSuccessVendorName(null);
        }
    }, [modalState.isOpen]);

    useEffect(() => {
        if (modalState.isOpen && modalState.step === "browse" && activeProperty) {
            hitTinderMultiLeadModalGtmEvent({action: "view"});
        }
    }, [activeProperty?.id, modalState.isOpen, modalState.step]);

    useEffect(() => {
        if (modalState.isOpen && modalState.step === "summary") {
            hitTinderMultiLeadSummaryGtmEvent({action: "view", offersCount: modalState.sentApplications.length});
        }
        // sentApplications is final once the summary step is reached, so it can't retrigger this effect.
    }, [modalState.isOpen, modalState.step, modalState.sentApplications.length]);

    useEffect(() => {
        return () => {
            if (successTimeoutRef.current) {
                clearTimeout(successTimeoutRef.current);
            }
        };
    }, []);

    if (!modalState.isOpen) {
        return null;
    }

    const isSummaryStep = modalState.step === "summary";

    const closeModal = () => {
        dispatch(closeTinderMultiLeadModal());
    };

    // User-initiated dismissal (X button / ESC) — the summary "Powróć do poszukiwań" CTA goes through
    // closeModal directly, tracking its own summary `click` instead of a `close`.
    const handleClose = () => {
        if (isSummaryStep) {
            hitTinderMultiLeadSummaryGtmEvent({action: "close", offersCount: modalState.sentApplications.length});
        } else {
            hitTinderMultiLeadModalGtmEvent({action: "close"});
        }

        closeModal();
    };

    const handleReject = () => {
        if (!activeProperty || isAsking || isContactDataLoading || isAdvancing) {
            return;
        }

        hitTinderMultiLeadModalGtmEvent({action: "click", clickText: "Odrzuć"});
        trackTinderMultiLeadRate({property: activeProperty, value: "dislike"});
        dispatch(rejectCurrentTinderMultiLeadProperty(activeProperty.id));
    };

    const handleAsk = () => {
        if (!activeProperty || isAsking || isContactDataLoading || isAdvancing) {
            return;
        }

        askSelectedQuestionsRef.current = [];
        hitTinderMultiLeadModalGtmEvent({action: "click", clickText: "Zapytaj"});
        hitTinderMultiLeadTopicGtmEvent({action: "view"});
        trackTinderMultiLeadRate({property: activeProperty, value: "like"});
        trackTinderMultiLeadSurveyStart({property: activeProperty});
        setIsAskSheetOpen(true);
    };

    const handleAskSheetClose = () => {
        if (activeProperty) {
            trackTinderMultiLeadSurveyClose({property: activeProperty, selectedQuestionValues: askSelectedQuestionsRef.current});
        }

        hitTinderMultiLeadTopicGtmEvent({action: "close"});
        setIsAskSheetOpen(false);
    };

    const handleAskSubmit = async (additionalQuestions: {value: string; label: string}[]) => {
        if (!activeProperty || isAsking || isContactDataLoading) {
            return;
        }

        hitTinderMultiLeadTopicGtmEvent({action: "submit", selectedOptions: additionalQuestions.map((question) => question.label)});

        const sentVendorName = activeProperty.offer.vendor.name;

        const application = await sendTinderMultiLeadApplication({
            additionalQuestions,
            property: activeProperty,
            refererApplicationUuid: modalState.refererApplicationUuid,
            sourceSection: modalState.sourceSection
        });

        if (application) {
            if (successTimeoutRef.current) {
                clearTimeout(successTimeoutRef.current);
            }

            setSuccessVendorName(sentVendorName);
            successTimeoutRef.current = setTimeout(() => setSuccessVendorName(null), 4000);
        }
    };

    return (
        <>
            <Modal
                isOpen={modalState.isOpen}
                onModalClose={handleClose}
                shouldCloseOnOverlayClick={false}
                zIndex={leadModalZindex}
                showCloseButton={false}
                fullScreen="xs"
                vAlign={isSummaryStep ? "center" : "bottom"}
                className={isSummaryStep ? modalContentSummaryStyle : modalContentStyle}
                contentLabel="Przejrzyj i zapytaj"
            >
                <div className={modalShellStyle}>
                    <button type="button" className={closeButtonStyle} onClick={handleClose} aria-label="Zamknij">
                        <CloseIcon size="1.6" wrapperSize="1.6" fill="currentColor" />
                    </button>

                    {modalState.step === "summary" ? (
                        <TinderMultiLeadSummary sentApplications={modalState.sentApplications} onClose={closeModal} />
                    ) : (
                        <>
                            <div className={headerRegionStyle}>
                                <header className={headerStyle}>
                                    <Text variant="headline_3" as="h2" color="var(--colors-secondary)">
                                        <Highlight>Przejrzyj i zapytaj</Highlight>
                                    </Text>
                                    <Text variant="body_copy_2" color="var(--colors-gray-700)">
                                        Przyspiesz swoje poszukiwania, skorzystaj z naszych rekomendacji.
                                    </Text>
                                </header>
                                {successVendorName && <TinderMultiLeadSuccessHeader reduceMotion={shouldReduceMotion} vendorName={successVendorName} />}
                            </div>

                            <div ref={contentRef} className={cx(contentStyle, isAdvancing ? contentLockedStyle : contentScrollStyle)}>
                                {isQueueLoading ? (
                                    <div className={loaderStyle}>
                                        <Loader size="lg" />
                                    </div>
                                ) : (
                                    <motion.div
                                        ref={setDeckNode}
                                        className={cx(deckStyle, showBacking && deckBackingSpaceStyle)}
                                        animate={{minHeight: isAdvancing && frozenHeight ? frozenHeight : 0}}
                                        transition={{duration: isAdvancing ? 0 : shouldReduceMotion ? 0.18 : 0.4, ease: [0.4, 0, 0.2, 1]}}
                                    >
                                        <TinderMultiLeadDeckShadows reduceMotion={shouldReduceMotion} shadowKeys={shadowKeys} />
                                        <TinderMultiLeadDeckPlaceholder active={isAdvancing} hasDeckBehind={showBacking} reduceMotion={shouldReduceMotion} />

                                        <AnimatePresence mode="popLayout" onExitComplete={handleExitComplete}>
                                            {activeProperty && (
                                                <TinderMultiLeadSwipeCard key={activeProperty.id}>
                                                    <TinderMultiLeadOfferCard
                                                        error={modalState.error}
                                                        index={modalState.currentIndex}
                                                        property={activeProperty}
                                                        variables={variables}
                                                        onReadyChange={handleCardReady}
                                                    />
                                                </TinderMultiLeadSwipeCard>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                )}
                            </div>

                            {activeProperty && (
                                <TinderMultiLeadBottomBar
                                    askDisabled={isAsking || isQueueLoading || isContactDataLoading || isAdvancing}
                                    isAsking={isAsking}
                                    onAsk={handleAsk}
                                    onReject={handleReject}
                                    rejectDisabled={isAsking || isContactDataLoading || isAdvancing}
                                />
                            )}
                        </>
                    )}
                </div>
            </Modal>

            <TinderMultiLeadAskSheet
                isOpen={isAskSheetOpen}
                isSubmitting={isAsking}
                vendorLogoUrl={askVendorLogoUrl}
                vendorName={askVendorName}
                onClose={handleAskSheetClose}
                onSelectedQuestionsChange={(questions) => {
                    askSelectedQuestionsRef.current = questions;
                }}
                onSubmit={handleAskSubmit}
            />
        </>
    );
}

const modalContentStyle = css({
    // The overlay is `position: fixed; inset: 0`, i.e. the VISIBLE viewport. 100vh would overflow it
    // on mobile browsers with dynamic UI (Android nav/URL bar), hiding the bottom bar and card end.
    width: "100%",
    height: "100%",
    maxHeight: "100%",
    overflow: "hidden",
    borderRadius: 0,
    backgroundColor: "var(--colors-white)",
    md: {
        width: "min(985px, calc(100vw - 32px))",
        height: "auto",
        maxHeight: "calc(100vh - 48px)",
        // Docked to the viewport bottom (vAlign="bottom"): round only the top corners.
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0
    }
});

const modalContentSummaryStyle = css({
    width: "100%",
    height: "100%",
    maxHeight: "100%",
    overflow: "hidden",
    borderRadius: 0,
    backgroundColor: "var(--colors-white)",
    md: {
        width: "min(985px, calc(100vw - 32px))",
        height: "auto",
        maxHeight: "calc(100vh - 48px)",
        borderRadius: 2
    }
});

const modalShellStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    maxHeight: "100%",
    backgroundColor: "var(--colors-white)",
    md: {
        minHeight: "auto",
        maxHeight: "calc(100vh - 48px)"
    }
});

const closeButtonStyle = css({
    position: "absolute",
    top: 2,
    right: 2,
    zIndex: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "3.2rem",
    height: "3.2rem",
    border: 0,
    borderRadius: "100%",
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-2)",
    cursor: "pointer"
});

const headerRegionStyle = css({
    position: "relative"
});

const headerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
    px: 2,
    pt: 2.5,
    pb: 2,
    pr: 8,
    md: {
        px: 4,
        pt: 3,
        pb: 2.5,
        pr: 9
    }
});

const contentStyle = css({
    position: "relative",
    // 80vh is only the PREFERRED size (keeps the modal tall while the deck loads). minHeight: 0 lets
    // the flex item shrink to the space left by the header/bottom bar — a hard minHeight would push
    // the scroll container's bottom past the modal's overflow:hidden edge, making the card end
    // unreachable by scrolling.
    flex: "1 1 80vh",
    minHeight: 0,
    overflowX: "hidden",
    scrollbarGutter: "stable",
    maxWidth: "calc(100% - 1.2rem)",
    px: 1.5,
    pt: 1.5,
    pb: 3,
    md: {
        px: 4,
        pt: 2,
        pb: 3
    }
});

const contentScrollStyle = css(prettyScroll);

const contentLockedStyle = css({
    overflowY: "hidden"
});

const deckStyle = css({
    position: "relative"
});

const deckBackingSpaceStyle = css({
    pt: "56px"
});

const loaderStyle = css({
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "var(--colors-white)"
});
